// Entry point: sync the header badges, build the first city and wire the UI.

import { updateLabels, updateModeLabel, updateTargetLabel } from './dom.js';
import { makeCity } from './city.js';
import { wireInput } from './input.js';
import { startLoop } from './render/loop.js';
import { dealCpuKit, initProgress, resetSpecialUses, savedMode } from './progress.js';
import { savedLang, setLang } from './i18n.js';
import { applyMode } from './modes.js';
import { randomSeed } from './rng.js';
import { initAudio, savedMusic, savedVolumes, setMusic, unlockMusicOnFirstGesture } from './audio.js';
import { state } from './state.js';

// Before anything reads a stat: a fresh install has none, and a returning player
// has whatever they earned last time.
initProgress();
// Then the language, before a single label is painted. El idioma de la RUTA manda si la
// página viene con uno: las versiones por idioma del build (/es/, /it/…) lo llevan en
// `data-lang` y quien entra por un enlace a /es/ quiere español aunque tenga otro guardado
// (y los buscadores necesitan que cada URL sea de verdad de su idioma). En la raíz no hay
// atributo y se usa lo que el jugador eligió la última vez, o el inglés.
setLang(document.documentElement.dataset.lang || savedLang());
// And their own mind about the music, for the same reason.
state.musicOn = savedMusic();

// And the two levels, saved the same way and for the same reason: settings, not progress, so
// wiping a save does not march the mix back to the middle.
const volumes = savedVolumes();
state.musicVolume = volumes.music;
state.fxVolume = volumes.fx;

// The music belongs to the splash screen rather than to the first menu, so this is
// where it is asked for — before a single screen is shown. Whether it can actually
// start here is the browser's decision and not ours: a page nobody has touched
// gets a suspended AudioContext, and there is no code that talks it round. A
// context that comes back running means this browser is willing, which is usually
// the case for somebody who has been here before, and the song starts now, intro
// and all, over the logo. A suspended one is one touch away instead, and the touch
// that skips the splash is the touch that starts it.
if (state.musicOn) {
  // The question is not "is it running now". A context does not report itself as
  // running the instant it is built — asking once and walking away walks away every
  // single time, which is exactly what the first version of this did. initAudio
  // creates the context and unlocks it, and the promise it returns settles when the
  // browser has made up its mind: a tick later where it allows audio, after the
  // first touch where it does not.
  initAudio().then(() => setMusic(true)).catch(() => {});

  // Armed as well, and harmless if the line above got there first: setMusic does
  // nothing when the music is already playing.
  unlockMusicOnFirstGesture();
}
// The mode has to be resolved before the first city is built, because wind range,
// skyline heights and gravity all come out of its settings.
applyMode(savedMode());
// Loading the page is the start of a match as far as the specials are concerned,
// so a returning player does not find them all showing as spent. Each side gets
// its own bag here for the same reason: the front end draws a city, and the kit
// of the side standing in it has to exist before anything reads it.
state.special = [null, null];
state.guard = [null, null];
state.specialUses = [resetSpecialUses(), dealCpuKit()];

updateTargetLabel();
updateModeLabel();
updateLabels();

// The first city needs a seed of its own, or every visit would open on the very
// same skyline.
state.matchSeed = randomSeed();
makeCity();
wireInput();
startLoop();
